"use client";

import React from 'react';
import { useRouter } from "next/navigation";
import { Check, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface PricingCardProps {
  plan: {
    name: string;
    icon: React.ReactNode;
    price: string;
    description: string;
    keyFeatures: string[];
    cta: string;
    popular: boolean;
    contactPage?: boolean;
  };
  isAnnual: boolean;
}

export default function PricingCard({ plan, isAnnual }: PricingCardProps): React.ReactElement {
  const router = useRouter();

  return (
    <div className="relative w-full h-full flex">
      {plan.popular && (
        <div className="absolute -top-4 left-0 right-0 mx-auto z-50">
          <div className="bg-emerald-500 text-white px-3 py-1 text-sm font-medium rounded-full w-fit mx-auto">
            Most Popular
          </div>
        </div>
      )}
      <div
        className={`group/card relative flex-1 overflow-hidden rounded-2xl p-px 
          ${plan.popular ? 'bg-white' : 'bg-[#0F0F0F]'}
          before:pointer-events-none before:absolute before:-left-40 before:-top-40 before:z-10 
          before:h-80 before:w-80 before:translate-x-[var(--mouse-x)] before:translate-y-[var(--mouse-y)] 
          before:rounded-full before:bg-white/80 before:opacity-0 before:blur-3xl before:transition-opacity 
          before:duration-500 after:pointer-events-none after:absolute after:-left-48 after:-top-48 
          after:z-30 after:h-64 after:w-64 after:translate-x-[var(--mouse-x)] 
          after:translate-y-[var(--mouse-y)] after:rounded-full after:bg-white after:opacity-0 
          after:blur-3xl after:transition-opacity after:duration-500 after:hover:opacity-20 
          before:group-hover/card:opacity-100
          hover:before:opacity-100 sm:before:opacity-0`}
      >
        <div className={`relative z-20 h-full flex flex-col p-4 sm:p-6 rounded-[inherit] ${plan.popular ? 'bg-white' : 'bg-black'}`}>
          <div className="flex items-center justify-between mb-6 sm:mb-8">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${plan.popular ? 'bg-gray-100' : 'bg-white/5'}`}>
                {plan.icon}
              </div>
              <h3 className={`text-lg sm:text-xl font-semibold ${plan.popular ? 'text-black' : 'text-white'}`}>
                {plan.name}
              </h3>
            </div>
          </div>

          <div className={`border-t border-b py-6 sm:py-8 mb-6 sm:mb-8 ${plan.popular ? 'border-gray-200' : 'border-gray-800'}`}>
            <div className="flex items-baseline justify-center">
              {plan.price === "Custom" ? (
                <span className={`text-4xl sm:text-5xl font-bold ${plan.popular ? 'text-black' : 'text-white'}`}>
                  Custom
                </span>
              ) : (
                <>
                  <span className={`text-4xl sm:text-5xl font-semibold ${plan.popular ? 'text-black' : 'text-white'}`}>$</span>
                  <span className={`text-4xl sm:text-5xl font-bold ${plan.popular ? 'text-black' : 'text-white'}`}>
                    {plan.price}
                  </span>
                  <span className={`ml-2 ${plan.popular ? 'text-gray-600' : 'text-gray-400'}`}>
                    {isAnnual ? '/mo' : '/mo'}
                  </span>
                </>
              )}
            </div>
            <p className={`text-sm mt-2 text-center ${plan.popular ? 'text-gray-600' : 'text-gray-400'}`}>
              {plan.description}
            </p>
          </div>

          <div className="flex-grow space-y-3 sm:space-y-4 mb-6 sm:mb-8">
            {plan.keyFeatures.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className={`w-5 h-5 mt-0.5 rounded-full flex items-center justify-center flex-shrink-0
                  ${plan.popular ? 'bg-emerald-500' : 'bg-emerald-500/20'}`}
                >
                  <Check className={`w-3 h-3 ${plan.popular ? 'text-white' : 'text-emerald-500'}`} />
                </div>
                <span className={`text-xs sm:text-sm text-left ${plan.popular ? 'text-black' : 'text-white'}`}>
                  {feature}
                </span>
              </div>
            ))}
          </div>

          <Button
            onClick={() => router.push('/auth/signup')}
            className={`w-full py-4 sm:py-6 text-sm sm:text-base ${
              plan.popular 
                ? 'bg-black text-white hover:bg-gray-900' 
                : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            {plan.cta}
            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}